import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import FolderIcon from "@mui/icons-material/Folder";
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import { useAuth } from "../../auth/AuthContext";
import {
  removePortfolioItem,
  addPortfolioItem,
  downloadCompanyReport,
  listPortfolios,
  listPortfolioItems,
  getSavedSearchesForCompany,
} from "../../data/request";
import { DELTA_ACCENT, BORDER_GREY, TEXT_SECONDARY } from "../colors";
import { EnterprisePill } from "../Components/EnterprisePill";
import { AddToPortfolioButton } from "../Components/AddToPortfolioButton";

type Portfolio = { portfolio_id: string; name: string };
type DynamicPortfolioMembership = { search_id: string; name: string };

const DEFAULT_PORTFOLIO_ID = "default";

// Renders in two places at once: the star + Export PDF row stays exactly
// where it always has (top-right of the header), while the portfolio
// membership pills + "Add to Portfolio" button are teleported (via a portal)
// into `portfoliosSlot`, a full-width DOM node the parent renders below a
// divider under the whole header row. Using a single component instance with
// a portal - rather than splitting into two components - means there's still
// only one set of hooks/effects/fetches, exactly as before; only the visual
// position of half the output changes.
export const CompanyActions = (props: { companyNo: string; companyName: string; portfoliosSlot: HTMLElement | null }) => {
  const { companyNo, companyName, portfoliosSlot } = props;
  const auth = useAuth();
  const [portfolios, setPortfolios] = useState<Portfolio[]>();
  const [memberPortfolios, setMemberPortfolios] = useState<Portfolio[]>([]);
  const [dynamicMemberPortfolios, setDynamicMemberPortfolios] = useState<DynamicPortfolioMembership[]>([]);
  const [isFavourited, setIsFavourited] = useState(false);
  const [favouriteBusy, setFavouriteBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const refreshMembership = async (list: Portfolio[]) => {
    const memberOf: Portfolio[] = [];
    await Promise.all(
      list.map(async (p) => {
        const doc = await listPortfolioItems(p.portfolio_id);
        const items: { company_number: string }[] = doc?.items ?? [];
        if (items.some((i) => i.company_number === companyNo)) {
          memberOf.push(p);
        }
        if (p.portfolio_id === DEFAULT_PORTFOLIO_ID) {
          setIsFavourited(items.some((i) => i.company_number === companyNo));
        }
      })
    );
    setMemberPortfolios(memberOf);
  };

  useEffect(() => {
    if (!auth.isPro) {
      return;
    }
    listPortfolios().then((doc) => {
      const list: Portfolio[] = doc?.portfolios ?? [{ portfolio_id: DEFAULT_PORTFOLIO_ID, name: "Favourites" }];
      setPortfolios(list);
      refreshMembership(list);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isPro, companyNo]);

  // Dynamic Portfolio membership - Enterprise-only, matching how Dynamic
  // Portfolios are gated everywhere else in this app (PortfolioPage.tsx's
  // "dynamic" tab, the ProGate on it, etc.). A Pro (non-Enterprise) or
  // signed-out visitor never has any saved searches to belong to, so this
  // simply doesn't fetch for them rather than showing a disabled control.
  useEffect(() => {
    if (!auth.isEnterprise) {
      setDynamicMemberPortfolios([]);
      return;
    }
    getSavedSearchesForCompany(companyNo).then((doc) => {
      setDynamicMemberPortfolios(doc?.dynamic_portfolios ?? []);
    });
  }, [auth.isEnterprise, companyNo]);

  // A signed-out visitor never fetches portfolios (see the effect above), so
  // portfolios stays undefined for them forever - only block rendering on a
  // missing portfolios list while we're actually expecting one to arrive for
  // a real signed-in user. This lets a signed-out visitor see the same
  // layout (star / Add to Portfolio / Export PDF), just disabled, instead of
  // this component vanishing entirely.
  const isSignedOut = !auth.isPro;
  if (!isSignedOut && !portfolios) {
    return null;
  }

  const namedPortfolios = (portfolios ?? []).filter((p) => p.portfolio_id !== DEFAULT_PORTFOLIO_ID);
  // A company's favourite status is already shown via the star icon right
  // next to it, so the default/Favourites portfolio would be redundant here -
  // only named (non-default) portfolios render as membership pills.
  const namedMemberPortfolios = memberPortfolios.filter((p) => p.portfolio_id !== DEFAULT_PORTFOLIO_ID);

  const toggleFavourite = async () => {
    setFavouriteBusy(true);
    try {
      if (isFavourited) {
        await removePortfolioItem(DEFAULT_PORTFOLIO_ID, companyNo);
      } else {
        await addPortfolioItem(DEFAULT_PORTFOLIO_ID, companyNo, companyName);
      }
      await refreshMembership(portfolios ?? []);
    } finally {
      setFavouriteBusy(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      await downloadCompanyReport(companyNo);
    } finally {
      setExporting(false);
    }
  };

  // Star + Export PDF - stays exactly where it always has, top-right of the
  // header row. Unchanged from before other than the Add to Portfolio button
  // (and its two flanking dividers) having moved out into portfoliosContent
  // below; the one remaining divider still separates the two groups.
  const topContent = (
    <Box style={{ display: "flex", gap: 8, alignItems: "center", opacity: isSignedOut ? 0.6 : 1 }}>
      <Tooltip title={isSignedOut ? "Sign in to add to Favourites" : isFavourited ? "Remove from Favourites" : "Add to Favourites"}>
        <span style={{ display: "flex" }}>
          <IconButton onClick={toggleFavourite} disabled={isSignedOut || favouriteBusy} style={{ color: DELTA_ACCENT }}>
            {isFavourited ? <StarIcon /> : <StarBorderIcon />}
          </IconButton>
        </span>
      </Tooltip>

      <Box sx={{ borderLeft: "1px solid", borderColor: BORDER_GREY, height: 32, marginX: 1.5 }} />

      <Tooltip title={isSignedOut ? "Sign in to export PDF reports" : !auth.isEnterprise ? "Upgrade to Enterprise to export PDF reports" : ""}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={isSignedOut || !auth.isEnterprise || exporting}
            onClick={handleExportPdf}
            style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
          >
            {exporting ? "Exporting..." : "Export PDF"}
          </Button>
          {!auth.isEnterprise && <EnterprisePill />}
        </span>
      </Tooltip>
    </Box>
  );

  // Dynamic Portfolios pills, then static Portfolios pills, then the Add to
  // Portfolio button - in that order, full width, below the new divider.
  // Dynamic Portfolios is Enterprise-only (see the fetch effect above), so a
  // Pro or signed-out visitor can never have any - the group is omitted for
  // them entirely rather than showing a label that can never have pills.
  // Static "In Portfolios" is shown for any signed-in visitor (Pro or
  // Enterprise), matching who the underlying listPortfolios() fetch runs
  // for, even if a Pro user's own list is empty because named portfolios are
  // an Enterprise-only capability.
  const portfoliosContent = (
    <Box style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16, opacity: isSignedOut ? 0.6 : 1 }}>
      {auth.isEnterprise && (
        <Box style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="caption" style={{ color: TEXT_SECONDARY }}>
            In Dynamic Portfolios: {dynamicMemberPortfolios.length === 0 && "None"}
          </Typography>
          {dynamicMemberPortfolios.map((p) => (
            <Chip
              key={p.search_id}
              icon={<DynamicFeedIcon style={{ color: DELTA_ACCENT }} />}
              label={p.name}
              size="small"
              variant="outlined"
              clickable
              component={RouterLink}
              to="/portfolio"
              state={{ tab: "dynamic", searchId: p.search_id }}
              style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
            />
          ))}
        </Box>
      )}

      {!isSignedOut && (
        <Box style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="caption" style={{ color: TEXT_SECONDARY }}>
            In Portfolios: {namedMemberPortfolios.length === 0 && "None"}
          </Typography>
          {namedMemberPortfolios.map((p) => (
            <Chip
              key={p.portfolio_id}
              icon={<FolderIcon style={{ color: DELTA_ACCENT }} />}
              label={p.name}
              size="small"
              variant="outlined"
              clickable
              component={RouterLink}
              to="/portfolio"
              style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
            />
          ))}
        </Box>
      )}

      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <AddToPortfolioButton
          portfolios={namedPortfolios}
          companyNumber={companyNo}
          companyName={companyName}
          disabled={isSignedOut || !auth.isEnterprise}
          disabledTooltip={
            isSignedOut
              ? "Sign in to organise companies into portfolios"
              : "Upgrade to Enterprise to organise companies into multiple portfolios"
          }
          onAdded={() => refreshMembership(portfolios ?? [])}
        />
        {!auth.isEnterprise && <EnterprisePill />}
      </span>
    </Box>
  );

  return (
    <>
      {topContent}
      {portfoliosSlot && createPortal(portfoliosContent, portfoliosSlot)}
    </>
  );
};

export default CompanyActions;
